import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useToast } from "../../components/ToastContext";
import { api } from '../../services/api';

const AdminDialog = ({ open, onClose, adminData, onSuccess }) => {
  const theme = useTheme();
  const toast = useToast();
  const userId = sessionStorage.getItem('userId');

  const [formData, setFormData] = useState({
    masterKey: '',
    masterValue: '',
    isActive: true,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && adminData) {
      setFormData({
        masterKey: adminData.masterKey || '',
        masterValue: adminData.masterValue || '',
        isActive: adminData.isActive !== undefined ? adminData.isActive : true,
      });
    } else if (open && !adminData) {
      setFormData({
        masterKey: '',
        masterValue: '',
        isActive: true,
      });
    }
  }, [open, adminData]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const getErrorMessage = (error) => {
    if (!error.response) return "Network error. Please check your connection.";
    const { status, data } = error.response;
    if (status === 400) return data?.message || "Invalid data provided.";
    if (status === 401) return "Unauthorized. Please login again.";
    if (status === 403) return "You don't have permission to perform this action.";
    if (status === 409) return "Admin key already exists. Please use a different key.";
    if (status >= 500) return "Server error. Please try again later.";
    return data?.message || `Error: ${status}`;
  };

  const handleSubmit = async () => {
    if (!formData.masterKey.trim()) {
      toast.error("Admin Key is required.");
      return;
    }
    if (!formData.masterValue.trim()) {
      toast.error("Admin Key Value is required.");
      return;
    }
    if (!userId) {
      toast.error("User ID not found. Please login again.");
      return;
    }

    try {
      setLoading(true);
      let response;
      const headers = {
        'X-CORRELATION-ID': 'd7b649f1-d9c9-4e4b-a82e-7d9b1ccb908c',
        'Content-Type': 'application/json',
      };

      if (adminData) {
        const updatePayload = {
          accountKey: formData.masterKey.trim(),
          accountKeyValue: formData.masterValue.trim(),
          updatedBy: userId,
          updatedTime: new Date().toISOString(),
          isActive: formData.isActive,
        };
        response = await api.post('/AccountSettingDetails/UpdateAccountSettingDetails', updatePayload, { headers });
      } else {
        const createPayload = [
          {
            AccountKey: formData.masterKey.trim(),
            AccountKeyValue: formData.masterValue.trim(),
            CreatedOn: new Date().toISOString(),
            UpdatedOn: new Date().toISOString(),
            isActive: formData.isActive,
            ClientId: 1, 
          }
        ];
        response = await api.post('/AccountSettingDetails/CreateAccountSettingDetails', createPayload, { 
            headers: { ...headers, accept: 'text/plain' } 
        });
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(`Admin configuration ${adminData ? 'updated' : 'added'} successfully!`);
        onSuccess(); 
        onClose();  
      }
    } catch (error) {
      console.error("Error saving admin config:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: theme.palette.background.default, pb: 1, mb: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
          {adminData ? 'Edit Admin Configuration' : 'Add Admin Configuration'}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ bgcolor: theme.palette.background.paper }}>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <TextField
            margin="dense"
            label="Admin Key *"
            placeholder="Enter Admin Key"
            type="text"
            fullWidth
            value={formData.masterKey}
            onChange={handleInputChange('masterKey')}
            disabled={!!adminData} 
          />
          <TextField
            margin="dense"
            label="Admin Key Value *"
            placeholder="Enter Admin Key Value"
            type="text"
            fullWidth
            value={formData.masterValue}
            onChange={handleInputChange('masterValue')}
          />
        </Box>

        <Box
          sx={{
            mt: 2,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label={<Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>Active</Typography>}
          />
        </Box>
        {!adminData && (
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
            Check this box to make the configuration active immediately.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, bgcolor: theme.palette.background.default }}>
        <Button onClick={onClose} variant="outlined" 
    sx={{ 
      p: 0.7,
      px: 2,
      color: theme.palette.mode === 'dark' ? 'grey.300' : 'text.secondary',
      borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.400',
      '&:hover': {
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
        borderColor: theme.palette.mode === 'dark' ? 'grey.500' : 'grey.600',
      }
    }} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" sx={{py:1.2}} disabled={loading || !formData.masterKey.trim() || !formData.masterValue.trim()}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminDialog;